import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { abilitySubject, useAbility } from '@/hooks/useAbility';
import { getShareableSurveysQueryOptions } from '@/services/conversations/shareableSurveys';

/**
 * The surveys the viewer may share, for the support composer's picker. Fetched
 * only while `enabled`, so the list loads when the picker opens.
 *
 * Each survey is asked of the viewer's rules as a record, so the picker offers
 * exactly what the send would accept: the server's `share`/`Survey` rule is
 * conditioned on the survey's status, and the bare subject would only say that
 * some survey may be shared.
 */
export function useShareableSurveys({ enabled }: { enabled: boolean }) {
  const ability = useAbility();

  const query = useQuery({ ...getShareableSurveysQueryOptions(), enabled });

  const surveys = useMemo(
    () => query.data?.filter((survey) => ability.can('share', abilitySubject('Survey', survey))),
    [ability, query.data]
  );

  return {
    surveys,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
